import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { GlobalStore } from '../../store';
import { useParams } from "react-router-dom";
import { subscribe, unsubscribe } from '../../actions/queueActions';
import { setTitle } from '../../actions/titleActions';
import { openShowMotdModal } from '../../actions/modalActions';
import Queue from '../../models/Queue';
import User from '../../models/User';
import RequestStatus from '../../enums/RequestStatus';
import EnterQueueViewComponent from './EnterQueue';
import QueueAdministratorOptionsViewComponent from './QueueAdministratorOptions';
import QueueEntryTableViewComponent from './QueueEntryTable';
import ReportForDuty from './ReportForDuty';
import OnDuty from './OnDuty';
import TodayBookings from './TodayBookings';
import PageNotFound from '../NoMatch';
import SearchViewComponent from '../../viewcomponents/Search';
import { Lock } from '../../viewcomponents/FontAwesome';
import DingLing from '../../sounds/DingLing.mp3';
import axios from 'axios';
import { HTTP_SERVER_URL } from '../../configuration';
import { sortWithBookingPromotion } from '../../utils/queueSort';

const CHECKIN_EXPIRY_MS = 12 * 60 * 60 * 1000; // 12 hours

interface TaOnDuty {
  username: string;
  realname: string;
}

function loadCheckinState(queueName: string): 1 | 2 | 3 {
  try {
    const raw = localStorage.getItem(`checkin_${queueName}`);
    if (!raw) return 1;
    const { state, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CHECKIN_EXPIRY_MS) {
      localStorage.removeItem(`checkin_${queueName}`);
      return 1;
    }
    return state as 1 | 2 | 3;
  } catch {
    return 1;
  }
}

function saveCheckinState(queueName: string, state: 1 | 2 | 3): void {
  localStorage.setItem(`checkin_${queueName}`, JSON.stringify({ state, timestamp: Date.now() }));
}

export default (): JSX.Element | null => {

  const { queueName } = useParams();

  const user = useSelector<GlobalStore, User | null>(store => store.user);
  const queue = useSelector<GlobalStore, Queue | null>(store => store.queues.queueList.filter(q => q.name === queueName)[0] || null);
  const queuesAreLoaded = useSelector<GlobalStore, boolean>(store => store.queues.requestStatus === RequestStatus.Success);
  const playSounds = useSelector<GlobalStore, boolean>(store => store.playSounds);

  const [filter, setFilter] = useState('');
  const [previousQueueEntryCount, setPreviousQueueEntryCount] = useState(0);
  const [hasShownMotd, setHasShownMotd] = useState(false);
  const [checkinState, setCheckinState] = useState<1 | 2 | 3>(1);
  const [onDutyTas, setOnDutyTas] = useState<TaOnDuty[]>([]);
  const [onDutyLoaded, setOnDutyLoaded] = useState(false);
  const [onDutyRefresh, setOnDutyRefresh] = useState(0);
  const prevLockedRef = useRef<boolean | null>(null);
  const latestQueueRef = useRef(queue);
  const latestUserRef = useRef(user);

  const dispatch = useDispatch();

  // Load checkin state from localStorage when queue is known. Reset the on-duty
  // snapshot so reconciliation waits for a fresh fetch for the new queue rather
  // than acting on the previous queue's list.
  useEffect(() => {
    if (queueName) {
      setCheckinState(loadCheckinState(queueName));
      setOnDutyTas([]);
      setOnDutyLoaded(false);
    }
  }, [queueName]);

  const isTAHere = user !== null && queueName !== undefined
    && (user.isTeacherIn(queueName) || user.isAssistantIn(queueName));

  // Poll the server's on-duty list (the same source of truth the On duty widget
  // uses: DB rows within a 1h inactivity window). Refetch immediately whenever a
  // duty action bumps onDutyRefresh.
  useEffect(() => {
    if (!isTAHere || !queueName || !user) return;
    let cancelled = false;
    const fetchOnDuty = () => {
      fetch(`${HTTP_SERVER_URL}/api/queues/${queueName}/on_duty`, {
        headers: { Authorization: `Token ${user.token}` }
      })
        .then(r => r.json())
        .then(d => {
          if (cancelled) return;
          // Only trust a well-formed response. On a transient error we keep the
          // previous list rather than wiping it (which would wrongly demote the
          // button to "Check in").
          if (Array.isArray(d)) {
            setOnDutyTas(d);
            setOnDutyLoaded(true);
          }
        })
        .catch(() => { /* keep previous list; don't reconcile on a failed poll */ });
    };
    fetchOnDuty();
    const id = setInterval(fetchOnDuty, 15_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [queueName, isTAHere, onDutyRefresh]);

  // Keep the duty button in sync with the server's on-duty list. The button used
  // to be driven purely by localStorage (12h expiry), so it could show "Check
  // out" long after the 1h inactivity window had dropped the TA from the list.
  // We only touch the two "settled" states: demote a lone "Check out" (state 3)
  // to "Check in" when the server has dropped the TA, and promote "Check in"
  // (state 1) to "Check out" when the server already considers them on duty.
  // State 2 (the brief cancel grace right after check-in) is deliberately left
  // alone — its on_duty row may not have committed yet when the post-check-in
  // refetch races in, and demoting it here would stomp a just-clicked check-in.
  // The guard means a consistent state is a no-op, so depending on checkinState
  // here can't loop.
  useEffect(() => {
    if (!isTAHere || !user || !queueName || !onDutyLoaded) return;
    const onList = onDutyTas.some(t => t.username === user.username);
    let next: 1 | 2 | 3 | null = null;
    if (!onList && checkinState === 3) next = 1;
    else if (onList && checkinState === 1) next = 3;
    if (next !== null) {
      setCheckinState(next);
      saveCheckinState(queueName, next);
    }
  }, [onDutyTas, onDutyLoaded, isTAHere, user, queueName, checkinState]);

  const savedScrollY = useRef(0);

  // Continuously track the user's real scroll position. Recording it only at
  // render-commit time (as before) left a stale value, so live queue updates
  // force-scrolled the page back to the top.
  useEffect(() => {
    const onScroll = () => { savedScrollY.current = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Before the browser paints a queue-entries change, restore the user's scroll
  // position if it moved. React preserves scroll on in-place DOM updates, so this
  // is usually a no-op; it only corrects the rare case where a content-height
  // change clamps the scroll.
  useLayoutEffect(() => {
    if (window.scrollY !== savedScrollY.current) {
      window.scrollTo({ top: savedScrollY.current, behavior: 'instant' as ScrollBehavior });
    }
  }, [queue?.queueEntries]);

  // Keep refs fresh so the title interval always reads latest state
  useEffect(() => { latestQueueRef.current = queue; }, [queue]);
  useEffect(() => { latestUserRef.current = user; }, [user]);

  // Update tab title every 15s to reflect booking promotion reordering
  // between WS events. Writes document.title directly — no render needed.
  useEffect(() => {
    const id = setInterval(() => {
      const q = latestQueueRef.current;
      const u = latestUserRef.current;
      if (!q) return;
      const sorted = sortWithBookingPromotion(q.queueEntries);
      if (u) {
        const i = sorted.findIndex(e => e.providerId === u.providerId);
        if (i !== -1) {
          document.title = `[${i+1}/${sorted.length}] ${q.name} | Stay A While`;
          return;
        }
      }
      document.title = `[${sorted.length}] ${q.name} | Stay A While`;
    }, 15_000);
    return () => clearInterval(id);
  }, []);

  // Detect locked → unlocked transition: reset checkin state to 1
  useEffect(() => {
    if (!queue) return;
    const wasLocked = prevLockedRef.current;
    const isLocked = queue.locked;
    if (wasLocked === true && isLocked === false) {
      // Queue just reopened — reset duty state
      const newState: 1 = 1;
      setCheckinState(newState);
      if (queueName) saveCheckinState(queueName, newState);
    }
    prevLockedRef.current = isLocked;
  }, [queue?.locked]);

  useEffect(() => {
    if (queue !== null) {
      if (queuesAreLoaded && !hasShownMotd && queue.motd) {
        dispatch(openShowMotdModal(queue.motd));
        setHasShownMotd(true);
      }
    }
  }, [queuesAreLoaded, queue, dispatch]);

  useEffect(() => {
    if (queueName) localStorage.setItem('lastVisitedQueue', queueName);
  }, [queueName]);

  useEffect(() => {
    if (queueName !== null && queueName !== undefined && queuesAreLoaded) {
      dispatch(subscribe(queueName));
      return () => {
        dispatch(unsubscribe(queueName));
      };
    }
  }, [queueName, queuesAreLoaded, dispatch]);

  useEffect(() => {
    if (!queue || !queuesAreLoaded) return;
    const existing: {name: string, timestamp: number}[] = JSON.parse(localStorage.getItem('RecentQueues') || '[]');
    const updated = [{name: queue.name, timestamp: Date.now()}, ...existing.filter(e => e.name !== queue.name)];
    localStorage.setItem('RecentQueues', JSON.stringify(updated.slice(0, 20)));
  }, [queue?.name, queuesAreLoaded]);

  useEffect(() => {
    if (queue !== null) {
      if (user !== null) {
        const sorted = sortWithBookingPromotion(queue.queueEntries);
        for (let i = 0; i < sorted.length; i++) {
          if (sorted[i].providerId === user.providerId) {
            dispatch(setTitle(`[${i+1}/${sorted.length}] ${queue.name} | Stay A While`));
            return;
          }
        }
      }
      dispatch(setTitle(`[${queue.queueEntries.length}] ${queue.name} | Stay A While`));
    }
  }, [queue, user, dispatch]);

  useEffect(() => {
    if (!queue) {
      return;
    }

    if (!user?.isTeacherIn(queue.name) && !user?.isAssistantIn(queue.name)) {
      return;
    }

    if (!queue.queueEntries) {
      setPreviousQueueEntryCount(0);
    }
    else {
      const newEntryNumber = queue.queueEntries.length;
      if (previousQueueEntryCount === 0 && newEntryNumber === 1 && playSounds) {
        new Audio(DingLing).play().catch(err => {
          console.warn('Queue sound blocked or failed to play:', err);
        });
      }
      setPreviousQueueEntryCount(newEntryNumber);
    }
  }, [queue?.queueEntries]);

  function handleCheckin() {
    if (!user || !queueName) return;
    axios.post(
      `${HTTP_SERVER_URL}/api/queues/${queueName}/checkin`,
      {},
      { headers: { Authorization: `Token ${user.token}` } }
    ).catch(() => {});
    const newState: 2 = 2;
    setCheckinState(newState);
    saveCheckinState(queueName, newState);
    setOnDutyRefresh(r => r + 1);
  }

  function handleCancelCheckin() {
    if (!user || !queueName) return;
    axios.delete(
      `${HTTP_SERVER_URL}/api/queues/${queueName}/checkin`,
      { headers: { Authorization: `Token ${user.token}` } }
    ).catch(() => {});
    const newState: 1 = 1;
    setCheckinState(newState);
    saveCheckinState(queueName, newState);
    setOnDutyRefresh(r => r + 1);
  }

  function handleCheckout() {
    if (!user || !queueName) return;
    axios.post(
      `${HTTP_SERVER_URL}/api/queues/${queueName}/checkout`,
      {},
      { headers: { Authorization: `Token ${user.token}` } }
    ).catch(() => {});
    const newState: 1 = 1;
    setCheckinState(newState);
    saveCheckinState(queueName, newState);
    setOnDutyRefresh(r => r + 1);
  }

  function handleHelpStarted() {
    if (!queueName) return;
    const newState: 3 = 3;
    setCheckinState(newState);
    saveCheckinState(queueName, newState);
  }

  return (
    !queuesAreLoaded
      ? null
      : queue === null
        ? <PageNotFound />
        : <div className="container" style={{ overflowX: 'hidden' }}>
            <div className="row">
              {
                queue.locked
                  ? <h1 className="col-12 col-lg-3 text-danger">{queue.name} <Lock /></h1>
                  : <h1 className="col-12 col-lg-3 theme-text">{queue.name}</h1>
              }
              <p className="col-12 col-lg-6 gray-text">{queue.info}</p>
              <div className="col-12 col-lg-3">
                <SearchViewComponent filter={filter} setFilter={setFilter} />
              </div>
            </div>
            <div className="row" style={{marginTop: '1.5em'}}>
              <div className="col-12 col-lg-3">
                <EnterQueueViewComponent queueName={queue.name} />
                <ReportForDuty
                  queueName={queue.name}
                  queueLocked={queue.locked}
                  checkinState={checkinState}
                  onCheckin={handleCheckin}
                  onCancel={handleCancelCheckin}
                  onCheckout={handleCheckout}
                />
                <QueueAdministratorOptionsViewComponent queue={queue} onPurgeConfirmed={() => setOnDutyRefresh(r => r + 1)} />
                <TodayBookings queueName={queue.name} />
                <OnDuty queueName={queue.name} tas={onDutyTas} />
              </div>
              <div className="col-12 col-lg-9">
                <QueueEntryTableViewComponent
                  filter={filter}
                  queueEntries={queue.queueEntries}
                  queueName={queue.name}
                  showHelperToAssistants={queue.showHelperToAssistants}
                  onHelpStarted={handleHelpStarted} />
              </div>
            </div>
          </div>
  );
};
